/**
 * Migration Wizard — R2 static-handoff (browser-only pipeline).
 *
 * The full migration pipeline (parse, map, generate, document build,
 * report-model build, HTML guide render) now runs entirely in the
 * browser via {@link runMigrationPipeline} + {@link generateHtml}.
 * No XML, no parsed config, no credentials, no session state ever
 * leave the user's tab.
 *
 * Result-page artifacts:
 *  - Self-contained HTML guide: rendered client-side via
 *    {@link generateHtml} from the rich `MigrationDocument` the
 *    pipeline already builds, opened in a new tab via a blob URL.
 *  - Velocity JSON: built client-side from the pipeline result and
 *    downloaded via an in-memory blob URL.
 *
 * The Node render endpoint (`/api/render`) and the PDF / DOCX download
 * buttons were archived in R2 to enable a static deliverable. The
 * historical implementations are preserved in git history; see
 * `docs/archived-components.md`.
 */

import { runMigrationPipeline, type RunMigrationPipelineResult } from './pipeline.js';
import { generateHtml } from '../../docs/html-generator.js';
import {
  parseProject,
  serializeProject,
  MAX_PROJECT_FILE_BYTES,
  type SavedProjectFile,
} from './project-file.js';
import type { OrchestratorProgressEvent } from './orchestrator.js';
import type { TargetPlatform, VelocityIconMap } from '../../types/velocity.js';
import type { MigrationStrategy } from '../../types/api.js';

interface CalciteButtonElement extends HTMLElement {
  disabled?: boolean;
  loading?: boolean;
  iconStart?: string;
}

interface CalciteSwitchElement extends HTMLElement {
  checked?: boolean;
  disabled?: boolean;
}

interface CalciteNoticeElement extends HTMLElement {
  kind?: 'brand' | 'info' | 'success' | 'warning' | 'danger';
  open?: boolean;
}

type NoticeKind = 'info' | 'success' | 'warning' | 'error' | 'danger' | 'brand';

/**
 * Temporary demo flag — when `false`, hides the Save Project download
 * button on the Results page and skips wiring the Load Project button on
 * Step 1. Flip back to `true` (and remove the `hidden` attribute on the
 * `#loadProjectGroup` element in `public/index.html`) to restore the
 * Save/Load Project workflow.
 */
const SHOW_PROJECT_SAVE_LOAD = false;

function requireEl<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Wizard: missing required element #${id}`);
  return el as T;
}

export function initMigrationWizard(): void {
  let currentStep = 1;
  /** Result of the most recent pipeline run, used to power downloads. */
  let pipelineResult: RunMigrationPipelineResult | null = null;
  /**
   * Velocity component icons for the guide's Velocity-styled flow diagram.
   * Fetched lazily (and memoized) during the processing phase rather than
   * bundled into the wizard, so the icon payload never weighs down the initial
   * page load or the authentication gate. Empty when the fetch is unavailable
   * or fails — the diagram then falls back to kind-colored glyphs.
   */
  let velocityIcons: VelocityIconMap = {};
  let velocityIconsPromise: Promise<VelocityIconMap> | null = null;
  /** Object URLs created for download buttons; revoked on reset. */
  const objectUrlsToRevoke: string[] = [];

  const steps = document.querySelectorAll<HTMLElement>('.step-tab');
  const panels = document.querySelectorAll<HTMLElement>('.step-panel');
  const nextBtn = requireEl<CalciteButtonElement>('nextBtn');
  const backBtn = requireEl<CalciteButtonElement>('backBtn');
  const fileInput = requireEl<HTMLInputElement>('fileInput');
  const fileName = requireEl<HTMLElement>('fileName');
  const dropZone = requireEl<HTMLElement>('dropZone');
  const alertsDiv = requireEl<HTMLElement>('alerts');
  const sessionStatus = requireEl<HTMLElement>('sessionStatus');
  const mainContent = requireEl<HTMLElement>('mainContent');
  const loadingOverlay = requireEl<HTMLElement>('loading');

  function goToStep(step: number): void {
    currentStep = step;

    steps.forEach((tab, index) => {
      const tabStep = index + 1;
      tab.classList.remove('active', 'completed', 'disabled');
      tab.removeAttribute('aria-current');
      if (tabStep === step) {
        tab.classList.add('active');
        tab.setAttribute('aria-current', 'step');
        tab.setAttribute('aria-disabled', 'false');
        tab.tabIndex = 0;
      } else if (tabStep < step) {
        tab.classList.add('completed');
        tab.setAttribute('aria-disabled', 'false');
        tab.tabIndex = 0;
      } else {
        tab.classList.add('disabled');
        tab.setAttribute('aria-disabled', 'true');
        tab.tabIndex = -1;
      }
    });

    panels.forEach((panel, index) => {
      const panelActive = index + 1 === step;
      panel.classList.toggle('active', panelActive);
      panel.setAttribute('aria-hidden', String(!panelActive));
    });

    backBtn.classList.toggle('is-hidden', step <= 1);

    if (step === 1) {
      nextBtn.textContent = 'Next';
      nextBtn.disabled = !fileInput.files?.length;
    } else if (step === 2) {
      nextBtn.textContent = 'Generate Migration Package';
      nextBtn.disabled = false;
    } else if (step === 3) {
      nextBtn.textContent = 'Start New Migration';
      nextBtn.disabled = false;
      sessionStatus.textContent = '';
    }

    focusStepTitle(step);
  }

  function focusStepTitle(step: number): void {
    const title = document.getElementById(`step${step}Title`);
    if (title) {
      (title as HTMLElement).focus({ preventScroll: true });
    }
  }

  function canOpenStep(step: number): boolean {
    return step <= currentStep;
  }

  function openStepFromTab(tab: HTMLElement): void {
    const step = Number(tab.dataset.step);
    if (!Number.isInteger(step) || !canOpenStep(step)) return;
    goToStep(step);
  }

  function clearAlerts(): void {
    while (alertsDiv.firstChild) alertsDiv.removeChild(alertsDiv.firstChild);
    alertsDiv.removeAttribute('role');
    alertsDiv.setAttribute('aria-live', 'polite');
  }

  function addNotice(kind: NoticeKind, title: string, messages: string | string[]): void {
    const alertKind: CalciteNoticeElement['kind'] =
      kind === 'error' ? 'danger' : (kind as CalciteNoticeElement['kind']);
    alertsDiv.setAttribute('role', alertKind === 'danger' ? 'alert' : 'status');
    alertsDiv.setAttribute('aria-live', alertKind === 'danger' ? 'assertive' : 'polite');

    const notice = document.createElement('calcite-notice') as CalciteNoticeElement;
    notice.kind = alertKind;
    notice.open = true;

    if (title) {
      const titleDiv = document.createElement('div');
      titleDiv.slot = 'title';
      titleDiv.textContent = title;
      notice.appendChild(titleDiv);
    }

    const messageDiv = document.createElement('div');
    messageDiv.slot = 'message';
    messageDiv.className = 'notice-message';
    messageDiv.textContent = Array.isArray(messages) ? messages.join('\n') : messages;
    notice.appendChild(messageDiv);
    alertsDiv.appendChild(notice);
  }

  function showAlert(kind: NoticeKind, message: string): void {
    clearAlerts();
    addNotice(kind, '', message);
    alertsDiv.tabIndex = -1;
    alertsDiv.focus({ preventScroll: true });
  }

  fileInput.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      fileName.textContent = target.files[0]!.name;
      nextBtn.disabled = false;
    }
  });

  dropZone.addEventListener('click', () => fileInput.click());

  dropZone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fileInput.click();
    }
  });

  steps.forEach((tab) => {
    tab.addEventListener('click', () => openStepFromTab(tab));
    tab.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openStepFromTab(tab);
      }
    });
  });

  dropZone.addEventListener('dragover', (e) => {
    e.preventDefault();
    dropZone.classList.add('dragover');
  });

  dropZone.addEventListener('dragleave', () => {
    dropZone.classList.remove('dragover');
  });

  dropZone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropZone.classList.remove('dragover');
    const dt = e.dataTransfer;
    if (dt && dt.files.length > 0) {
      fileInput.files = dt.files;
      fileName.textContent = dt.files[0]!.name;
      nextBtn.disabled = false;
    }
  });

  // ---- Save / Load Project (resume downloads without re-uploading XML) ----
  // Skipped entirely when SHOW_PROJECT_SAVE_LOAD is false. The HTML element
  // group is also `hidden` so the description copy doesn't render.
  const loadProjectBtn = SHOW_PROJECT_SAVE_LOAD
    ? document.getElementById('loadProjectBtn') as CalciteButtonElement | null
    : null;
  const loadProjectInput = SHOW_PROJECT_SAVE_LOAD
    ? document.getElementById('loadProjectInput') as HTMLInputElement | null
    : null;
  if (loadProjectBtn && loadProjectInput) {
    loadProjectBtn.addEventListener('click', () => loadProjectInput.click());
    loadProjectInput.addEventListener('change', () => {
      const file = loadProjectInput.files?.[0];
      if (!file) return;
      void handleLoadProject(file)
        .finally(() => {
          // Reset the input so re-selecting the same file fires `change` again.
          loadProjectInput.value = '';
        });
    });
  }

  backBtn.addEventListener('click', () => {
    if (currentStep > 1) goToStep(currentStep - 1);
  });

  nextBtn.addEventListener('click', async () => {
    if (currentStep === 1) advanceToOptions();
    else if (currentStep === 2) await runMigration();
    else if (currentStep === 3) resetMigration();
  });

  function advanceToOptions(): void {
    if (!fileInput.files?.length) return;
    clearAlerts();
    goToStep(2);
  }

  function resetMigration(): void {
    pipelineResult = null;
    while (objectUrlsToRevoke.length > 0) {
      const url = objectUrlsToRevoke.pop();
      if (url) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // best-effort
        }
      }
    }
    fileInput.value = '';
    fileName.textContent = '';
    const downloadLinks = document.getElementById('downloadLinks');
    if (downloadLinks) while (downloadLinks.firstChild) downloadLinks.removeChild(downloadLinks.firstChild);
    sessionStatus.textContent = '';
    clearAlerts();
    goToStep(1);
  }

  /**
   * Warn the user before leaving the page if a pipeline run has produced
   * downloadable results that have not been explicitly discarded via the
   * Start-over flow. The browser will only show a generic confirmation
   * dialog (modern browsers ignore custom messages), but setting
   * `returnValue` is still the documented way to opt into the warning.
   *
   * We never unregister this listener — it short-circuits when
   * `pipelineResult` is null, which is the state after `resetMigration()`.
   */
  function onBeforeUnload(event: BeforeUnloadEvent): void {
    if (pipelineResult === null) return;
    event.preventDefault();
    // Legacy: some older browsers required a returnValue to trigger the prompt.
    event.returnValue = '';
  }
  window.addEventListener('beforeunload', onBeforeUnload);

  /**
   * Lazily fetches the vendored Velocity icon set once and memoizes the
   * result. Resolves to an empty map (never rejects) so a missing or failed
   * fetch degrades gracefully to fallback glyphs in the flow diagram. The
   * relative `assets/...` URL mirrors how the migration worker is loaded, so
   * it resolves correctly under any deploy path prefix.
   */
  function loadVelocityIcons(): Promise<VelocityIconMap> {
    if (velocityIconsPromise) return velocityIconsPromise;
    velocityIconsPromise = fetch('assets/velocity-icons.json')
      .then((response) => (response.ok ? (response.json() as Promise<VelocityIconMap>) : ({} as VelocityIconMap)))
      .then((icons: VelocityIconMap) => {
        velocityIcons = icons && typeof icons === 'object' ? icons : {};
        return velocityIcons;
      })
      .catch(() => {
        velocityIcons = {};
        return velocityIcons;
      });
    return velocityIconsPromise;
  }

  async function runMigration(): Promise<void> {
    const file = fileInput.files?.[0];
    if (!file) {
      showAlert('error', 'Please choose a GeoEvent XML file before generating the migration package.');
      goToStep(1);
      return;
    }
    const targetPlatform = (requireEl<HTMLInputElement>('targetPlatform')).value as TargetPlatform;
    const migrationStrategy = (requireEl<HTMLInputElement>('migrationStrategy')).value as MigrationStrategy;
    // Phase 5 §7.8 public-access probes are opt-in via a Step 2
    // toggle. The element is a `calcite-switch`; `.checked` reflects
    // the runtime state. Default-on at page load (`checked` attribute
    // in markup); we read the live value here so prior toggles in
    // the same session are honored.
    const probesEnabled = window.OFFLINE_MODE !== true && requireEl<CalciteSwitchElement>('probesEnabled').checked !== false;
    // Schema flattening toggle (default on). When enabled, nested object and
    // array fields are flattened at the Velocity feed level and the Map Fields
    // tool references the resulting flattened scalar field names.
    const flattenNestedSchema = requireEl<CalciteSwitchElement>('flattenSchema').checked !== false;

    setLoading(true);
    clearAlerts();

    try {
      const onProgress = (event: OrchestratorProgressEvent): void => {
        sessionStatus.textContent = event.message;
      };

      const result = await runMigrationPipeline({
        file,
        targetPlatform,
        migrationStrategy,
        flattenNestedSchema,
        onProgress,
        orchestratorOptions: { probesEnabled },
      });

      pipelineResult = result;
      // Load the Velocity flow-diagram icons during the processing phase so
      // they are ready by the time the user opens or downloads the HTML guide
      // on the Results page. Fire-and-forget: the report generators await the
      // memoized promise themselves and fall back to glyphs if it is unready.
      void loadVelocityIcons();
      setLoading(false);
      sessionStatus.textContent = '';

      const summary = result.renderModel.summary;
      requireEl('fullyCompatible').textContent = String(summary.fullyMapped);
      requireEl('partiallyCompatible').textContent = String(summary.partiallyMapped);
      requireEl('noEquivalent').textContent = String(summary.unmapped);
      requireEl('componentTotal').textContent = `${summary.totalComponents} components analyzed`;

      if (result.errors.length > 0) {
        addNotice('danger', 'Errors', result.errors);
      }

      // When no input could be mapped to a Velocity feed, the generator returns
      // a null configuration (a Velocity JSON must contain at least one feed).
      // Surface a clear notice here so users understand why the JSON download
      // button is disabled — otherwise the disabled state looks like a bug.
      if (!result.hasVelocityJson) {
        addNotice(
          'warning',
          'No Velocity JSON generated',
          'None of the GeoEvent inputs in this configuration map to a Velocity feed type, so a Velocity JSON could not be created (a valid Velocity configuration requires at least one mapped feed). The Download Velocity JSON button is disabled for this reason. The migration guide is still available and lists each input with manual-action guidance for the missing feed equivalents.',
        );
      }

      const downloadLinks = requireEl<HTMLElement>('downloadLinks');
      while (downloadLinks.firstChild) downloadLinks.removeChild(downloadLinks.firstChild);

      // 1. Open HTML guide (browser-rendered, opens in a new tab).
      const openHtml = createDownloadButton(
        'openHtml',
        'launch',
        'Open Migration Guide (HTML)',
      );
      openHtml.addEventListener('click', () => openHtmlReport(openHtml));
      downloadLinks.appendChild(openHtml);

      // 1b. Download HTML guide as a .html file. Chrome doesn't expose
      //     a one-click "Save Page As" shortcut and most users won't
      //     discover Cmd/Ctrl+S, so we offer the same artifact as an
      //     explicit download. The generated HTML is self-contained
      //     (inline CSS + inline SVG) so it works fully offline once
      //     saved.
      const downloadHtml = createDownloadButton(
        'downloadHtml',
        'download',
        'Download Migration Guide (HTML)',
      );
      downloadHtml.addEventListener('click', () => downloadHtmlReport(downloadHtml));
      downloadLinks.appendChild(downloadHtml);

      // 2. Download Velocity JSON (built entirely client-side).
      const downloadJson = createDownloadButton(
        'downloadJson',
        'download',
        'Download Velocity JSON',
      );
      if (result.hasVelocityJson) {
        downloadJson.addEventListener('click', () => downloadVelocityJson(downloadJson));
      } else {
        downloadJson.disabled = true;
      }
      downloadLinks.appendChild(downloadJson);

      // 3. Save Project — exports the render model + Velocity JSON as a
      //    single .json file the user can re-load later to re-trigger any
      //    of the downloads without re-uploading the XML. No source XML,
      //    parser output, or mapping intermediates are saved.
      if (SHOW_PROJECT_SAVE_LOAD) {
        const saveProject = createDownloadButton(
          'saveProject',
          'save',
          'Save Project (.json)',
        );
        saveProject.addEventListener('click', () => saveCurrentProject(saveProject));
        downloadLinks.appendChild(saveProject);
      }

      // Warnings produced by the pipeline are surfaced inside the generated
      // guides (HTML / PDF / DOCX) under the "Migration Notes" section. We
      // intentionally do not duplicate them on the Results page — only blocking
      // failures (which throw and are caught below) are shown as alerts.

      goToStep(3);
    } catch (error) {
      setLoading(false);
      sessionStatus.textContent = '';
      const message = error instanceof Error ? error.message : String(error);
      showAlert('error', 'Migration failed: ' + message);
    }
  }

  async function openHtmlReport(button: CalciteButtonElement): Promise<void> {
    if (!pipelineResult) return;
    try {
      button.disabled = true;
      button.loading = true;
      // HTML is generated entirely in the browser from the rich
      // `MigrationDocument` the pipeline already builds. Flow diagrams
      // are emitted as inline SVG via `renderFlowDiagramSvg` (no Node
      // canvas dependency), so the static bundle has no /api/render
      // dependency for the HTML guide.
      await loadVelocityIcons();
      const html = generateHtml(pipelineResult.migrationDocument, { velocityIcons });
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      objectUrlsToRevoke.push(url);
      // Use a synthetic <a target="_blank" rel="noopener noreferrer"> click
      // rather than window.open(). window.open() returns null whenever the
      // `noopener` feature is set (per spec), which makes popup-blocker
      // detection unreliable — a successful open looks identical to a block.
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      // Use the HTML `hidden` attribute rather than `anchor.style.display`
      // so this code path remains CSP-compliant after `'unsafe-inline'` is
      // dropped from `style-src` / `style-src-attr` (see server.ts CSP).
      anchor.hidden = true;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      markDownloadComplete(button);
    } catch (error) {
      restoreDownloadButton(button);
      const message = error instanceof Error ? error.message : String(error);
      showAlert('error', 'Could not open HTML guide: ' + message);
    }
  }

  function downloadHtmlReport(button: CalciteButtonElement): void {
    if (!pipelineResult) return;
    const result = pipelineResult;
    button.disabled = true;
    button.loading = true;
    // Reuses the same in-browser generator as `openHtmlReport`. The
    // resulting document is self-contained (inline <style>, inline
    // SVG flow diagrams) and renders identically offline. Icons are
    // awaited via the memoized loader so the Velocity flow diagram is
    // fully styled even if the user clicks before the lazy fetch settles.
    loadVelocityIcons()
      .then(() => {
        const html = generateHtml(result.migrationDocument, { velocityIcons });
        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const filename = sanitizeHtmlGuideFilename(result.sourceFilenameDisplay);
        triggerBlobDownload(blob, filename);
        markDownloadComplete(button);
      })
      .catch((error: unknown) => {
        restoreDownloadButton(button);
        const message = error instanceof Error ? error.message : String(error);
        showAlert('error', 'Could not download HTML guide: ' + message);
      });
  }

  function sanitizeHtmlGuideFilename(source: string | undefined): string {
    const base = (source ?? 'migration')
      .replace(/\.[Xx][Mm][Ll]$/, '')
      .replace(/[^A-Za-z0-9._-]+/g, '_')
      .slice(0, 64) || 'migration';
    return `${base}.migration-guide.html`;
  }

  function downloadVelocityJson(button: CalciteButtonElement): void {
    if (!pipelineResult || !pipelineResult.hasVelocityJson) return;
    try {
      button.disabled = true;
      button.loading = true;
      const blob = new Blob([pipelineResult.velocityJsonText], { type: 'application/json' });
      triggerBlobDownload(blob, 'velocity-config.json');
      markDownloadComplete(button);
    } catch (error) {
      restoreDownloadButton(button);
      showAlert('error', 'Download failed: ' + (error instanceof Error ? error.message : String(error)));
    }
  }

  function saveCurrentProject(button: CalciteButtonElement): void {
    if (!pipelineResult) return;
    try {
      button.disabled = true;
      button.loading = true;
      const text = serializeProject({
        renderModel: pipelineResult.renderModel,
        velocityJsonText: pipelineResult.velocityJsonText,
        hasVelocityJson: pipelineResult.hasVelocityJson,
      });
      const filename = sanitizeProjectFilename(pipelineResult.sourceFilenameDisplay);
      triggerBlobDownload(new Blob([text], { type: 'application/json' }), filename);
      markDownloadComplete(button);
    } catch (error) {
      restoreDownloadButton(button);
      showAlert('error', 'Save failed: ' + (error instanceof Error ? error.message : String(error)));
    }
  }

  function sanitizeProjectFilename(source: string | undefined): string {
    const base = (source ?? 'migration')
      .replace(/\.[Xx][Mm][Ll]$/, '')
      .replace(/[^A-Za-z0-9._-]+/g, '_')
      .slice(0, 64) || 'migration';
    return `${base}.velocity-project.json`;
  }

  async function handleLoadProject(file: File): Promise<void> {
    if (file.size > MAX_PROJECT_FILE_BYTES) {
      showAlert('error', 'Project file is larger than the 32 MB load limit and cannot be opened.');
      return;
    }
    let text: string;
    try {
      text = await file.text();
    } catch (err) {
      showAlert('error', 'Could not read the project file: ' + (err instanceof Error ? err.message : String(err)));
      return;
    }
    const result = parseProject(text);
    if (!result.ok) {
      const message =
        result.reason === 'too-large' ? 'Project file exceeds the 32 MB load limit.' :
        result.reason === 'invalid-json' ? 'Project file is not valid JSON.' :
        result.reason === 'wrong-version' ? 'Project file was saved by an incompatible version of this tool.' :
        'Project file did not pass schema validation (it may be corrupt or tampered).';
      showAlert('error', message);
      return;
    }
    restorePipelineFromProject(result.project);
  }

  function restorePipelineFromProject(project: SavedProjectFile): void {
    // Mirror the post-migration state so the Step 3 download buttons work
    // without invoking the parser/mapper/generator pipeline. Fields that
    // are not needed for downloads are populated with safe placeholders.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    pipelineResult = {
      // The parser/mapper outputs are not needed for downloads and are
      // intentionally not captured in the saved project file. The
      // download paths only read renderModel / velocityJsonText.
      config: null as unknown as RunMigrationPipelineResult['config'],
      validation: { valid: true, errors: [], warnings: [] } as unknown as RunMigrationPipelineResult['validation'],
      mappingResult: null as unknown as RunMigrationPipelineResult['mappingResult'],
      velocityConfig: null,
      velocityJsonText: project.velocityJsonText,
      hasVelocityJson: project.hasVelocityJson,
      renderModel: project.renderModel,
      migrationDocument: null as unknown as RunMigrationPipelineResult['migrationDocument'],
      probeResults: [],
      sourceFilenameDisplay: project.renderModel.sourceFilenameDisplay ?? 'restored-project',
      warnings: [],
      errors: [],
    } as RunMigrationPipelineResult;

    // Drive the Step 3 UI off the restored model. The flow here mirrors
    // the end of `runMigration()` but without the parser/mapper work.
    const summary = project.renderModel.summary;
    requireEl('fullyCompatible').textContent = String(summary.fullyMapped);
    requireEl('partiallyCompatible').textContent = String(summary.partiallyMapped);
    requireEl('noEquivalent').textContent = String(summary.unmapped);
    requireEl('componentTotal').textContent = `${summary.totalComponents} components analyzed`;

    renderDownloadButtonsForRestoredProject();
    sessionStatus.textContent = '';
    clearAlerts();
    showAlert('info', 'Project loaded. Downloads are ready below.');
    goToStep(3);
  }

  function renderDownloadButtonsForRestoredProject(): void {
    const downloadLinks = requireEl<HTMLElement>('downloadLinks');
    while (downloadLinks.firstChild) downloadLinks.removeChild(downloadLinks.firstChild);

    const openHtml = createDownloadButton(
      'openHtml',
      'launch',
      'Open Migration Guide (HTML)',
    );
    openHtml.addEventListener('click', () => openHtmlReport(openHtml));
    downloadLinks.appendChild(openHtml);

    const downloadJson = createDownloadButton(
      'downloadJson',
      'download',
      'Download Velocity JSON',
    );
    if (pipelineResult?.hasVelocityJson) {
      downloadJson.addEventListener('click', () => downloadVelocityJson(downloadJson));
    } else {
      downloadJson.disabled = true;
    }
    downloadLinks.appendChild(downloadJson);

    const saveProject = SHOW_PROJECT_SAVE_LOAD
      ? createDownloadButton(
          'saveProject',
          'save',
          'Save Project (.json)',
        )
      : null;
    if (saveProject) {
      saveProject.addEventListener('click', () => saveCurrentProject(saveProject));
      downloadLinks.appendChild(saveProject);
    }
  }

  function triggerBlobDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    objectUrlsToRevoke.push(url);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Defer revocation slightly so the browser has a chance to start
    // the download. The URL will also be revoked on reset.
    setTimeout(() => {
      const idx = objectUrlsToRevoke.indexOf(url);
      if (idx >= 0) objectUrlsToRevoke.splice(idx, 1);
      try {
        URL.revokeObjectURL(url);
      } catch {
        // best-effort
      }
    }, 5000);
  }

  function setLoading(isLoading: boolean): void {
    loadingOverlay.classList.toggle('visible', isLoading);
    loadingOverlay.setAttribute('aria-hidden', String(!isLoading));
    mainContent.setAttribute('aria-busy', String(isLoading));
  }

  function createDownloadButton(
    id: string,
    icon: string,
    label: string,
  ): CalciteButtonElement {
    const button = document.createElement('calcite-button') as CalciteButtonElement;
    if (id) button.id = id;
    button.iconStart = icon;
    button.textContent = label;
    button.setAttribute('aria-label', label);
    return button;
  }

  function markDownloadComplete(button: CalciteButtonElement | null): void {
    if (!button) return;
    button.loading = false;
    button.disabled = false;
    // Visual confirmation only — swap the leading icon to a check.
    // The label text is intentionally NOT changed: appending "Again"
    // can grow the button wide enough to wrap to the next row when
    // multiple action buttons share a flex container.
    button.iconStart = 'check';
  }

  function restoreDownloadButton(button: CalciteButtonElement | null): void {
    if (!button) return;
    button.loading = false;
    button.disabled = false;
  }
}
